import type { ConstructionSowChatSessionView } from '../ConstructionSowChat/types';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function isServicesController(value: Record<string, unknown>): boolean {
  const workflowType = asRecord(value.workflow)?.workflow_type;
  return value.status === 'services_pws_chat' || workflowType === 'services' || workflowType === 'complex_services';
}

/**
 * A Construction SOW chat can be entered only from a server-issued controller
 * envelope. This intentionally contains no client keyword routing or stage id.
 */
export function extractConstructionSowRouterResponse(value: unknown): ConstructionSowChatSessionView | null {
  const root = asRecord(value);
  if (!root || isServicesController(root)) return null;
  // `postLazyCreateV2Turn` unwraps recognized controller envelopes before
  // returning them to HomeChatPanel. Accept that already-validated view on
  // its second routing pass as well; otherwise a direct first-message SOW
  // generation response falls through to the legacy `{ session_id, turn }`
  // branch and is rendered as a failed chat question.
  if (
    typeof root.session_id === 'string'
    && typeof root.home_chat_session_id === 'string'
    && typeof root.cursor === 'string'
    && asRecord(root.intake)
  ) {
    return root as unknown as ConstructionSowChatSessionView;
  }
  const candidate = root.status === 'construction_sow_chat' || root.status === 'handoff_ready'
    ? asRecord(root.construction_sow_chat)
    : null;
  if (!candidate || typeof candidate.session_id !== 'string' || isServicesController(candidate)) return null;
  const homeChatSessionId = typeof root.home_chat_session_id === 'string'
    ? root.home_chat_session_id
    : typeof candidate.home_chat_session_id === 'string' ? candidate.home_chat_session_id : undefined;
  return {
    ...(candidate as unknown as ConstructionSowChatSessionView),
    ...(homeChatSessionId ? { home_chat_session_id: homeChatSessionId } : {}),
  };
}
